import * as React from 'react'
import {
  ActivityType,
  ActivityTypeId,
  ChartScanActivity,
  ChartScanCountryChange,
  ChartScanDisc,
  ChartScanDistanceData,
  ChartScanDistancePoint,
  ChartScanVehicleUsed,
  ChartScanViolations,
} from '@/lib/api/company/chartScan'
import { useUndoRedo } from './useUndoRedo'
import {
  MINUTES_PER_DAY,
  parseChartIso,
  toChartIso,
} from '@/modules/imports/components/TachographChartCanvas'
import { clockMinute, dayLength, dayOffset, distancePerVehicle } from './chartDay'

// Convert any ISO timestamp to midnight of its calendar day. The chart's
// angular axis is anchored at midnight (matching WPF's
// TachographChartActivity.MinValue convention), so click-to-add and
// drag-the-junction operations must convert minute-of-day → ISO using
// midnight, not dayStart (which is 04:23 or wherever the boundary sits).
// Chart timestamps are naive wall-clock interpreted in UTC-space (see
// parseChartIso), so "midnight" is the UTC midnight of those literal components.
function midnightOf(iso: string): number {
  const d = parseChartIso(iso)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
}

// Immutable snapshot of everything WPF's TachographChartState captures. Pushed to
// the undo/redo stack on every mutation — so shift+click adding / clicking-to-remove
// distance points participates in undo/redo just like activity edits.
export type ChartEditorState = {
  activities: ChartScanActivity[]
  driverId: number | null
  vehicleId: number | null
  dayStartDate: string
  dayEndDate: string
  beginCountry: number | null
  endCountry: number | null
  noonShiftMinutes: number
  firstVehicleOdometerStart: number
  firstVehicleOdometerEnd: number
  distanceFromChart: number
  tachographStatus: number
  isCrewWork: boolean
  isClosed: boolean
  addManualEntryOnDayStart: boolean
  addManualEntryOnDayEnd: boolean
  dayNote: string
  violations: ChartScanViolations
  // Additional vehicle-change rows after the first. Empty by default.
  additionalVehiclesUsed: ChartScanVehicleUsed[]
  countryChanges: ChartScanCountryChange[]
  // Speed-trace ring geometry + the red (start) / cyan (end) distance points.
  // Editable via the canvas (shift+click to add, click to remove).
  distanceData: ChartScanDistanceData
}

export type ChartEditor = ChartEditorState & {
  // Selection / hover state — NOT pushed to undo (matches WPF: PropertyChanged for
  // SelectedActivityItem / IsActive is filtered out of OnChartImportPropertyChanged).
  selectedActivityIds: Set<number>
  hoveredActivityId: number | null
  hoverFromCanvas: boolean

  setSelected: (ids: number[]) => void
  setHovered: (id: number | null, fromCanvas: boolean) => void

  addActivity: (type: ActivityTypeId) => void
  addActivityAtMinute: (type: ActivityTypeId, minute: number) => void
  // sameDrag: continues the previous change, so the whole drag is one undo step.
  replaceActivities: (next: ChartScanActivity[], sameDrag?: boolean) => void
  deleteSelected: () => void
  deleteAllOfType: (type: ActivityTypeId) => void
  changeSelectedType: (type: ActivityTypeId) => void
  toggleSelectedFerry: () => void

  moveBoundary: (boundaryIndex: number, newMinute: number) => void
  // Moves the whole disc to another calendar day (YYYY-MM-DD); clock times stay.
  setChartDate: (date: string) => void
  setDayStartTime: (hhmm: string) => void
  setDayEndTime: (hhmm: string) => void
  setNoonShiftMinutes: (minutes: number) => void

  setDriverId: (id: number | null) => void
  setVehicleId: (id: number | null) => void
  setOdometerStart: (km: number) => void
  setOdometerEnd: (km: number) => void
  setFirstVehicle: (id: number | null, odometerStart: number, odometerEnd: number, byUser: boolean) => void
  setDistanceFromChart: (km: number) => void
  setTachographStatus: (status: number) => void
  setIsCrewWork: (v: boolean) => void
  setIsClosed: (v: boolean) => void
  setAddManualEntryOnDayStart: (v: boolean) => void
  setAddManualEntryOnDayEnd: (v: boolean) => void
  setBeginCountry: (country: number | null) => void
  setEndCountry: (country: number | null) => void
  setDayNote: (note: string) => void
  setViolation: (key: keyof ChartScanViolations, v: boolean) => void
  addVehicleChange: (atIso: string, vehicleId: number | null, odometerStart: number, odometerEnd: number) => void
  editVehicleChange: (vehiclesUsedId: number, atIso: string, vehicleId: number | null, odometerStart: number, odometerEnd: number) => void
  removeVehicleChange: (vehiclesUsedId: number) => void
  // Chart read-out a change at `atIso` would get (the dialog's live preview); null without distance points.
  distanceForChangeAt: (atIso: string, exceptId?: number) => number | null
  // WPF _isOdometerModifiedByUser: once typed, picking a vehicle no longer overwrites the odometer.
  isOdometerModifiedByUser: () => boolean
  updateVehicleChange: (vehiclesUsedId: number, patch: Partial<ChartScanVehicleUsed>) => void
  addCountryChange: (atIso: string, country: string) => void
  removeCountryChange: (countryChangeId: number) => void
  addDistancePoint: (kind: 'start' | 'end', point: ChartScanDistancePoint) => void
  removeDistancePoint: (kind: 'start' | 'end', index: number) => void

  undo: () => void
  redo: () => void
  canUndo: boolean
  canRedo: boolean
  reset: (next: ChartEditorState) => void
}

// Build the initial editor snapshot from a server-side disc DTO.
export function discToEditorState(disc: ChartScanDisc): ChartEditorState {
  return {
    activities: disc.activities,
    driverId: disc.driverId ?? null,
    vehicleId: disc.vehicleId ?? null,
    dayStartDate: disc.dayStartDate,
    dayEndDate: disc.dayEndDate,
    beginCountry: disc.beginCountry ?? null,
    endCountry: disc.endCountry ?? null,
    noonShiftMinutes: disc.noonShiftMinutes,
    firstVehicleOdometerStart: disc.firstVehicleOdometerStart,
    firstVehicleOdometerEnd: disc.firstVehicleOdometerEnd,
    distanceFromChart: disc.distanceFromChart,
    tachographStatus: disc.tachographStatus,
    isCrewWork: disc.isCrewWork,
    isClosed: disc.isClosed,
    addManualEntryOnDayStart: disc.addManualEntryOnDayStart,
    addManualEntryOnDayEnd: disc.addManualEntryOnDayEnd,
    dayNote: disc.dayNote,
    violations: disc.violations,
    additionalVehiclesUsed: disc.vehiclesUsed ?? [],
    countryChanges: disc.countryChanges ?? [],
    distanceData: disc.distanceData,
  }
}

export function useChartEditor(initialDisc: ChartScanDisc): ChartEditor {
  const initial = React.useMemo(() => discToEditorState(initialDisc), [initialDisc.discId])
  const { current, push, replaceTop, undo, redo, canUndo, canRedo, reset } = useUndoRedo(initial)

  // Re-seed when a different disc loads (navigation between discs).
  const odometerModified = React.useRef(false)
  React.useEffect(() => {
    reset(initial)
    odometerModified.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial])

  const [selectedActivityIds, setSelectedIds] = React.useState<Set<number>>(new Set())
  const [hoveredActivityId, setHoveredId] = React.useState<number | null>(null)
  const [hoverFromCanvas, setHoverFromCanvas] = React.useState(false)

  const setSelected = React.useCallback(
    (ids: number[]) => setSelectedIds(new Set(ids)),
    [],
  )
  const setHovered = React.useCallback((id: number | null, fromCanvas: boolean) => {
    setHoveredId(id)
    setHoverFromCanvas(fromCanvas)
  }, [])

  const mutate = React.useCallback(
    (patch: Partial<ChartEditorState>) => push({ ...current, ...patch }),
    [push, current],
  )

  // ----- Activity mutations (WPF AddNewActivity / DeleteActivities / ChangeActivityType / ChangeFerry) -----

  const addActivity = React.useCallback(
    (type: ActivityTypeId) => {
      // WPF AddNewActivity: insert AT the end of the selected activity (or first
      // activity start if nothing selected). Length defaults to 1 minute, expands
      // until next activity or day end.
      const selected = pickFirstSelected(current.activities, selectedActivityIds)
      const anchorIso = selected ? selected.endTime : current.activities[0]?.startTime
      if (!anchorIso) return
      const next = insertActivityAt(current.activities, type, anchorIso)
      mutate({ activities: next })
    },
    [current, selectedActivityIds, mutate],
  )

  const replaceActivities = React.useCallback(
    (next: ChartScanActivity[], sameDrag = false) => {
      if (sameDrag) replaceTop({ ...current, activities: next })
      else mutate({ activities: next })
    },
    [mutate, replaceTop, current],
  )

  const addActivityAtMinute = React.useCallback(
    (type: ActivityTypeId, minute: number) => {
      // Click-on-canvas in adding mode (WPF: ActivityAddingMode + click). Insert a
      // 1-minute activity centered on the clicked minute, splitting any existing
      // activity at that point. Anchor on midnight (chart's 12-o'clock position)
      // so the click's minute-of-day translates to the correct calendar time.
      const midnightMs = midnightOf(current.dayStartDate)
      const anchorMs = midnightMs + Math.max(0, Math.min(MINUTES_PER_DAY - 1, Math.round(minute))) * 60_000
      const anchorIso = toChartIso(new Date(anchorMs))
      const next = insertActivityAt(current.activities, type, anchorIso)
      mutate({ activities: next })
    },
    [current, mutate],
  )

  const deleteSelected = React.useCallback(() => {
    if (selectedActivityIds.size === 0) return
    const next = current.activities.filter((a) => !selectedActivityIds.has(a.activityId))
    setSelectedIds(new Set())
    mutate({ activities: stitchGaps(next, current.dayStartDate, current.dayEndDate) })
  }, [current, selectedActivityIds, mutate])

  const deleteAllOfType = React.useCallback(
    (type: ActivityTypeId) => {
      const next = current.activities.map((a) =>
        a.type === type ? { ...a, type: ActivityType.Rest } : a,
      )
      mutate({ activities: mergeSameAdjacent(next) })
    },
    [current, mutate],
  )

  const changeSelectedType = React.useCallback(
    (type: ActivityTypeId) => {
      if (selectedActivityIds.size === 0) return
      const next = current.activities.map((a) =>
        selectedActivityIds.has(a.activityId) ? { ...a, type } : a,
      )
      mutate({ activities: mergeSameAdjacent(next) })
    },
    [current, selectedActivityIds, mutate],
  )

  const toggleSelectedFerry = React.useCallback(() => {
    if (selectedActivityIds.size === 0) return
    const next = current.activities.map((a) =>
      selectedActivityIds.has(a.activityId)
        ? { ...a, ferryTrainCrossing: !a.ferryTrainCrossing }
        : a,
    )
    mutate({ activities: next })
  }, [current, selectedActivityIds, mutate])

  // ----- Boundary handles (drag the joint between two adjacent activities) -----

  const moveBoundary = React.useCallback(
    (boundaryIndex: number, newMinute: number) => {
      if (boundaryIndex <= 0 || boundaryIndex >= current.activities.length) return
      // Anchor on midnight — same reason as addActivityAtMinute.
      const midnightMs = midnightOf(current.dayStartDate)
      const next = current.activities.map((a) => ({ ...a }))
      const prev = next[boundaryIndex - 1]
      const cur = next[boundaryIndex]
      const prevStart = parseChartIso(prev.startTime).getTime() / 60000
      const curEnd = parseChartIso(cur.endTime).getTime() / 60000
      const newM = Math.max(prevStart + 1, Math.min(curEnd - 1, newMinute))
      const clampedIso = toChartIso(new Date(midnightMs + Math.round(newM * 60000)))
      prev.endTime = clampedIso
      cur.startTime = clampedIso
      mutate({ activities: next })
    },
    [current, mutate],
  )

  // ----- Form fields (every setter pushes a snapshot) -----

  const setChartDate = React.useCallback(
    (date: string) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return
      const on = (iso: string) => `${date}${iso.slice(10)}`
      mutate({
        dayStartDate: on(current.dayStartDate),
        dayEndDate: on(current.dayEndDate),
        activities: current.activities.map((a) => ({ ...a, startTime: on(a.startTime), endTime: on(a.endTime) })),
        additionalVehiclesUsed: current.additionalVehiclesUsed.map((v) => ({
          ...v,
          startTime: on(v.startTime),
          endTime: on(v.endTime),
        })),
        countryChanges: current.countryChanges.map((c) => ({ ...c, atTime: on(c.atTime) })),
      })
    },
    [current, mutate],
  )
  const setDayStartTime = React.useCallback(
    (hhmm: string) => mutate({ dayStartDate: replaceTime(current.dayStartDate, hhmm) }),
    [current.dayStartDate, mutate],
  )
  const setDayEndTime = React.useCallback(
    (hhmm: string) => mutate({ dayEndDate: replaceTime(current.dayEndDate, hhmm) }),
    [current.dayEndDate, mutate],
  )
  const setNoonShiftMinutes = React.useCallback(
    (m: number) => mutate({ noonShiftMinutes: m }),
    [mutate],
  )
  const setDriverId = React.useCallback((id: number | null) => mutate({ driverId: id }), [mutate])
  const setVehicleId = React.useCallback((id: number | null) => mutate({ vehicleId: id }), [mutate])
  const setOdometerStart = React.useCallback(
    (km: number) => {
      odometerModified.current = true
      mutate({ firstVehicleOdometerStart: km })
    },
    [mutate],
  )
  const setOdometerEnd = React.useCallback(
    (km: number) => {
      odometerModified.current = true
      mutate({ firstVehicleOdometerEnd: km })
    },
    [mutate],
  )
  // One snapshot for the first vehicle and its odometer (separate setters would overwrite each other).
  const setFirstVehicle = React.useCallback(
    (id: number | null, odometerStart: number, odometerEnd: number, byUser: boolean) => {
      if (byUser) odometerModified.current = true
      mutate({ vehicleId: id, firstVehicleOdometerStart: odometerStart, firstVehicleOdometerEnd: odometerEnd })
    },
    [mutate],
  )
  const setDistanceFromChart = React.useCallback(
    (km: number) => mutate({ distanceFromChart: km }),
    [mutate],
  )
  const setTachographStatus = React.useCallback(
    (s: number) => mutate({ tachographStatus: s }),
    [mutate],
  )
  const setIsCrewWork = React.useCallback((v: boolean) => mutate({ isCrewWork: v }), [mutate])
  const setIsClosed = React.useCallback((v: boolean) => mutate({ isClosed: v }), [mutate])
  const setAddManualEntryOnDayStart = React.useCallback(
    (v: boolean) => mutate({ addManualEntryOnDayStart: v }),
    [mutate],
  )
  const setAddManualEntryOnDayEnd = React.useCallback(
    (v: boolean) => mutate({ addManualEntryOnDayEnd: v }),
    [mutate],
  )
  const setDayNote = React.useCallback((n: string) => mutate({ dayNote: n }), [mutate])
  const setViolation = React.useCallback(
    (key: keyof ChartScanViolations, v: boolean) =>
      mutate({ violations: { ...current.violations, [key]: v } }),
    [current.violations, mutate],
  )

  // Halves cut by a change that moved away merge back, unless something else still sits on that instant.
  const uncutAt = React.useCallback(
    (iso: string, vehicles: ChartScanVehicleUsed[]) =>
      vehicles.some((v) => v.startTime === iso) || current.countryChanges.some((c) => c.atTime === iso)
        ? current.activities
        : mergeActivitiesAt(current.activities, iso),
    [current.activities, current.countryChanges],
  )

  const addVehicleChange = React.useCallback(
    (atIso: string, vehicleId: number | null, odometerStart: number, odometerEnd: number) => {
      const next: ChartScanVehicleUsed = {
        vehiclesUsedId: nextVehicleChangeId(current.additionalVehiclesUsed),
        vehicleId,
        startTime: atIso,
        endTime: current.dayEndDate,
        odometerStart,
        odometerEnd,
        distanceFromChart: 0,
      }
      // 1:1 with WPF CutActivityByVehicleChanges: split any activity that spans
      // the change instant so the change lands exactly on an activity boundary.
      mutate({
        activities: cutActivityAt(current.activities, atIso),
        additionalVehiclesUsed: linkVehicleChanges(
          [...current.additionalVehiclesUsed.filter((v) => v.startTime !== atIso), next],
          current.dayStartDate,
          current.dayEndDate,
        ),
      })
    },
    [current, mutate],
  )

  const editVehicleChange = React.useCallback(
    (id: number, atIso: string, vehicleId: number | null, odometerStart: number, odometerEnd: number) => {
      const target = current.additionalVehiclesUsed.find(v => v.vehiclesUsedId === id)
      if (!target) return
      const others = current.additionalVehiclesUsed.filter(v => v.vehiclesUsedId !== id && v.startTime !== atIso)
      mutate({
        activities:
          target.startTime === atIso
            ? current.activities
            : cutActivityAt(uncutAt(target.startTime, others), atIso),
        additionalVehiclesUsed: linkVehicleChanges(
          [...others, { ...target, startTime: atIso, vehicleId, odometerStart, odometerEnd }],
          current.dayStartDate,
          current.dayEndDate,
        ),
      })
    }, [current, mutate, uncutAt],
  )

  const removeVehicleChange = React.useCallback(
    (vehiclesUsedId: number) => {
      const target = current.additionalVehiclesUsed.find((v) => v.vehiclesUsedId === vehiclesUsedId)
      if (!target) return
      const others = current.additionalVehiclesUsed.filter((v) => v.vehiclesUsedId !== vehiclesUsedId)
      mutate({
        activities: uncutAt(target.startTime, others),
        additionalVehiclesUsed: linkVehicleChanges(others, current.dayStartDate, current.dayEndDate),
      })
    },
    [current, mutate, uncutAt],
  )

  const updateVehicleChange = React.useCallback(
    (vehiclesUsedId: number, patch: Partial<ChartScanVehicleUsed>) =>
      mutate({
        additionalVehiclesUsed: current.additionalVehiclesUsed.map((v) =>
          v.vehiclesUsedId === vehiclesUsedId ? { ...v, ...patch } : v,
        ),
      }),
    [current.additionalVehiclesUsed, mutate],
  )

  const addCountryChange = React.useCallback(
    (atIso: string, country: string) => {
      // Like vehicle changes, country boundaries also align to activity edges so
      // each saved activity attributes to one country.
      const splitActivities = cutActivityAt(current.activities, atIso)
      const id = nextCountryChangeId(current.countryChanges)
      // The previousCountry is whatever the most-recent change set it to.
      const ordered = [...current.countryChanges].sort(
        (a, b) => +new Date(a.atTime) - +new Date(b.atTime),
      )
      const previousCountry = ordered.length > 0 ? ordered[ordered.length - 1].country : ''
      const next: ChartScanCountryChange = {
        countryChangeId: id,
        country: country.toUpperCase(),
        previousCountry,
        atTime: atIso,
      }
      mutate({
        activities: splitActivities,
        countryChanges: [...current.countryChanges, next].sort(
          (a, b) => +new Date(a.atTime) - +new Date(b.atTime),
        ),
      })
    },
    [current, mutate],
  )

  const removeCountryChange = React.useCallback(
    (countryChangeId: number) =>
      mutate({
        countryChanges: current.countryChanges.filter((c) => c.countryChangeId !== countryChangeId),
      }),
    [current.countryChanges, mutate],
  )

  // ----- Distance points (WPF AddDistanceStartPoint / OnPreviewMouseLeftButtonDown remove) -----

  const addDistancePoint = React.useCallback(
    (kind: 'start' | 'end', point: ChartScanDistancePoint) => {
      const d = current.distanceData
      const next: ChartScanDistanceData =
        kind === 'start'
          ? { ...d, startPoints: [...d.startPoints, point] }
          : { ...d, endPoints: [...d.endPoints, point] }
      mutate({ distanceData: next })
    },
    [current.distanceData, mutate],
  )

  const removeDistancePoint = React.useCallback(
    (kind: 'start' | 'end', index: number) => {
      const d = current.distanceData
      const next: ChartScanDistanceData =
        kind === 'start'
          ? { ...d, startPoints: d.startPoints.filter((_, i) => i !== index) }
          : { ...d, endPoints: d.endPoints.filter((_, i) => i !== index) }
      mutate({ distanceData: next })
    },
    [current.distanceData, mutate],
  )

  // WPF recalculates the read-out on every change of points, vehicle changes and day boundaries.
  const chartDistances = React.useMemo(
    () => vehicleDistances(current.distanceData, current.additionalVehiclesUsed, current.dayStartDate, current.dayEndDate),
    [current.distanceData, current.additionalVehiclesUsed, current.dayStartDate, current.dayEndDate],
  )
  const additionalVehiclesUsed = React.useMemo(
    () =>
      chartDistances
        ? current.additionalVehiclesUsed.map((v) => ({
            ...v,
            distanceFromChart: chartDistances.byId.get(v.vehiclesUsedId) ?? 0,
          }))
        : current.additionalVehiclesUsed,
    [chartDistances, current.additionalVehiclesUsed],
  )
  const distanceForChangeAt = (atIso: string, exceptId?: number) => {
    const candidate = { vehiclesUsedId: Number.MIN_SAFE_INTEGER, startTime: atIso }
    const others = current.additionalVehiclesUsed.filter(
      (v) => v.vehiclesUsedId !== exceptId && v.startTime !== atIso,
    )
    const preview = vehicleDistances(current.distanceData, [...others, candidate], current.dayStartDate, current.dayEndDate)
    return preview ? (preview.byId.get(candidate.vehiclesUsedId) ?? 0) : null
  }

  return {
    ...current,
    distanceFromChart: chartDistances ? chartDistances.first : current.distanceFromChart,
    additionalVehiclesUsed,
    distanceForChangeAt,
    isOdometerModifiedByUser: () => odometerModified.current,
    selectedActivityIds,
    hoveredActivityId,
    hoverFromCanvas,
    setSelected,
    setHovered,
    addActivity,
    addActivityAtMinute,
    replaceActivities,
    deleteSelected,
    deleteAllOfType,
    changeSelectedType,
    toggleSelectedFerry,
    moveBoundary,
    setChartDate,
    setDayStartTime,
    setDayEndTime,
    setNoonShiftMinutes,
    setDriverId,
    setVehicleId,
    setOdometerStart,
    setOdometerEnd,
    setFirstVehicle,
    setDistanceFromChart,
    setTachographStatus,
    setIsCrewWork,
    setIsClosed,
    setAddManualEntryOnDayStart,
    setAddManualEntryOnDayEnd,
    setBeginCountry: (beginCountry) => mutate({ beginCountry }),
    setEndCountry: (endCountry) => mutate({ endCountry }),
    setDayNote,
    setViolation,
    addVehicleChange,
    editVehicleChange,
    removeVehicleChange,
    updateVehicleChange,
    addCountryChange,
    removeCountryChange,
    addDistancePoint,
    removeDistancePoint,
    undo,
    redo,
    canUndo,
    canRedo,
    reset: (next) => reset(next),
  }
}

// ---------- helpers ----------

function pickFirstSelected(
  activities: ChartScanActivity[],
  ids: Set<number>,
): ChartScanActivity | null {
  for (const a of activities) if (ids.has(a.activityId)) return a
  return null
}

function insertActivityAt(
  activities: ChartScanActivity[],
  type: ActivityTypeId,
  anchorIso: string,
): ChartScanActivity[] {
  // Find the activity that contains `anchorIso` (or starts after it). Split it
  // into [before-anchor, new, after-anchor].
  const t = parseChartIso(anchorIso).getTime()
  const next: ChartScanActivity[] = []
  for (const a of activities) {
    const s = parseChartIso(a.startTime).getTime()
    const e = parseChartIso(a.endTime).getTime()
    if (t > s && t < e) {
      // Split-around-anchor produces THREE activities. The first half keeps
      // the original `a.activityId`; the new one and the second half each
      // get a fresh id derived from `next` (so the second call sees the
      // first new id and avoids collision). Otherwise both halves of the
      // split share the same id and hovering one highlights both rows.
      next.push({ ...a, endTime: anchorIso })
      next.push({
        activityId: nextActivityId(next),
        startTime: anchorIso,
        endTime: toChartIso(new Date(t + 60_000)),
        type,
        // WPF flags only the midnight filler rests as manual entries.
        manualEntry: false,
        ferryTrainCrossing: false,
        crew: false,
      })
      next.push({
        ...a,
        activityId: nextActivityId(next),
        startTime: toChartIso(new Date(t + 60_000)),
      })
    } else {
      next.push(a)
    }
  }
  return mergeSameAdjacent(next)
}

function mergeSameAdjacent(activities: ChartScanActivity[]): ChartScanActivity[] {
  const out: ChartScanActivity[] = []
  for (const a of activities) {
    const last = out[out.length - 1]
    // The last activity may wrap past midnight (end < start); merging it would collapse the span.
    if (
      last &&
      last.type === a.type &&
      last.endTime === a.startTime &&
      parseChartIso(a.endTime) > parseChartIso(last.startTime)
    ) {
      out[out.length - 1] = { ...last, endTime: a.endTime }
    } else {
      out.push(a)
    }
  }
  return out
}

function stitchGaps(
  activities: ChartScanActivity[],
  dayStartIso: string,
  dayEndIso: string,
): ChartScanActivity[] {
  // After a delete, fill any gap by extending the previous activity's endTime to
  // match the next activity's startTime. Ensures the day stays continuous, like WPF.
  if (activities.length === 0) return activities
  const out: ChartScanActivity[] = []
  for (let i = 0; i < activities.length; i++) {
    const a = { ...activities[i] }
    if (i === 0 && a.startTime !== dayStartIso) a.startTime = dayStartIso
    const next = activities[i + 1]
    if (next && a.endTime !== next.startTime) a.endTime = next.startTime
    // The out-of-day remainder (starts where the day ends) closes the circle; leave it.
    const closesCircle = clockMinute(a.startTime) === clockMinute(dayEndIso)
    if (i === activities.length - 1 && !closesCircle && a.endTime !== dayEndIso) a.endTime = dayEndIso
    out.push(a)
  }
  return mergeSameAdjacent(out)
}

export function replaceTime(iso: string, hhmm: string): string {
  const [hStr, mStr] = hhmm.split(':')
  const h = parseInt(hStr, 10)
  const m = parseInt(mStr, 10)
  if (Number.isNaN(h) || Number.isNaN(m)) return iso
  const d = parseChartIso(iso)
  d.setUTCHours(h, m, 0, 0)
  return toChartIso(d)
}

// 1:1 with WPF TachographChart.CutActivity (TachographChart.cs:931+): if any
// activity strictly contains `cutIso` (start < cutIso < end), split it into
// two activities of the same type meeting at cutIso. If `cutIso` already lands
// on an activity boundary (or no activity contains it), no change.
function cutActivityAt(
  activities: ChartScanActivity[],
  cutIso: string,
): ChartScanActivity[] {
  const cut = +parseChartIso(cutIso)
  const out: ChartScanActivity[] = []
  for (const a of activities) {
    const s = +parseChartIso(a.startTime)
    const e = +parseChartIso(a.endTime)
    if (s < e ? cut > s && cut < e : s > e && (cut > s || cut < e)) {
      out.push({ ...a, endTime: cutIso })
      out.push({
        ...a,
        activityId: nextActivityId(out),
        startTime: cutIso,
        endTime: a.endTime,
      })
    } else {
      out.push(a)
    }
  }
  return out
}

function nextActivityId(existing: ChartScanActivity[]): number {
  // Stateless: derive a fresh negative id from the current minimum. Avoids the
  // module-level counter (which doesn't reset across disc loads and could
  // collide with a new disc's server-issued ids). Negative ids distinguish
  // client-side temp activities; the PUT endpoint wipes-and-rewrites anyway.
  return Math.min(0, ...existing.map((a) => a.activityId)) - 1
}

// Reverse of cutActivityAt: rejoin two identical halves meeting at `iso`.
function mergeActivitiesAt(
  activities: ChartScanActivity[],
  iso: string,
): ChartScanActivity[] {
  const i = activities.findIndex((a) => a.startTime === iso)
  const prev = activities[i - 1]
  const cur = activities[i]
  if (
    !prev ||
    prev.endTime !== iso ||
    prev.type !== cur.type ||
    prev.manualEntry !== cur.manualEntry ||
    prev.ferryTrainCrossing !== cur.ferryTrainCrossing ||
    prev.crew !== cur.crew ||
    clockSpan(prev) + clockSpan(cur) >= MINUTES_PER_DAY
  )
    return activities
  return [
    ...activities.slice(0, i - 1),
    { ...prev, endTime: cur.endTime },
    ...activities.slice(i + 1),
  ]
}

function clockSpan(a: ChartScanActivity): number {
  return dayOffset(clockMinute(a.endTime), clockMinute(a.startTime))
}

// Each vehicle runs until the next change (the last one until day end), counted from the day start.
function linkVehicleChanges(
  vehicles: ChartScanVehicleUsed[],
  dayStartIso: string,
  dayEndIso: string,
): ChartScanVehicleUsed[] {
  const dayStart = clockMinute(dayStartIso)
  const offset = (v: ChartScanVehicleUsed) => dayOffset(clockMinute(v.startTime), dayStart)
  const sorted = [...vehicles].sort((a, b) => offset(a) - offset(b))
  return sorted.map((v, i) => ({
    ...v,
    endTime: sorted[i + 1]?.startTime ?? dayEndIso,
  }))
}

// Live read-out per vehicle; null when the disc has no distance points to read.
function vehicleDistances(
  data: ChartScanDistanceData | null | undefined,
  vehicles: Pick<ChartScanVehicleUsed, 'vehiclesUsedId' | 'startTime'>[],
  dayStartIso: string,
  dayEndIso: string,
): { first: number; byId: Map<number, number> } | null {
  if (!data || data.startPoints.length + data.endPoints.length === 0) return null
  const dayStart = clockMinute(dayStartIso)
  const length = dayLength(dayStartIso, dayEndIso)
  const changes = vehicles
    .map((v) => ({ id: v.vehiclesUsedId, offset: dayOffset(clockMinute(v.startTime), dayStart) }))
    .filter((c) => c.offset > 0 && c.offset < length)
    .sort((a, b) => a.offset - b.offset)
  const km = distancePerVehicle(data, [0, ...changes.map((c) => c.offset)], dayStart, length)
  return { first: km[0], byId: new Map(changes.map((c, i) => [c.id, km[i + 1]])) }
}

let _vehicleChangeIdCounter = -1
function nextVehicleChangeId(existing: ChartScanVehicleUsed[]): number {
  if (_vehicleChangeIdCounter === -1) {
    _vehicleChangeIdCounter = Math.min(0, ...existing.map((v) => v.vehiclesUsedId)) - 1
  }
  return _vehicleChangeIdCounter--
}

let _countryChangeIdCounter = -1
function nextCountryChangeId(existing: ChartScanCountryChange[]): number {
  if (_countryChangeIdCounter === -1) {
    _countryChangeIdCounter = Math.min(0, ...existing.map((c) => c.countryChangeId)) - 1
  }
  return _countryChangeIdCounter--
}
